import Utils from "../../basic/Utils";
import {
  HeadingPitchRoll,
  Quaternion,
  JulianDate,
  Matrix3,
  Matrix4,
  Transforms,
  Cartesian3,
} from "@cesium/engine";

/**
 * Ditap Geometry Class Editing Class
 * @alias EditDitapGeometry
 * @private
 */
class EditDitapGeometry {
  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @returns {Matrix4 | undefined}
   */
  static getMatrix(entity) {
    return entity.computeModelMatrix();
  }

  /**
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @returns {BoundingSphere | undefined}
   */
  static getBoundingSphere(entity) {
    return entity.computeBoundingSphere();
  }

  /**
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @returns {Cartesian3 | undefined}
   */
  static getPosition(entity) {
    return Utils.getPropertyValue(entity.position);
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {Cartesian3 | Property} position
   * @returns {Cartesian3 | Property}
   */
  static setPosition(entity, position) {
    entity.position = position;
    return position;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {boolean} enu
   * @returns {Object}
   */
  static decomposition(entity, enu = true) {
    const matrix4 = entity.computeModelMatrix(new JulianDate()); // 원본 행렬 B

    const position = Utils.getPropertyValue(entity.position); // 현재 중심점
    const originalPosition = position;

    let scale;
    let translation;
    let quaternion;
    let headingPitchRoll;
    if (enu) {
      const originalEnu = Transforms.eastNorthUpToFixedFrame(originalPosition); // 원본 중심정의 EastNorthUp 행렬 A
      const originalEnuInverse = Matrix4.inverse(originalEnu, new Matrix4()); // Inverse A^{-1}

      // A^{-1}BA => ENU 행렬 제거한 행렬 추출
      const matrix4_2 = Matrix4.multiply(
        originalEnuInverse,
        matrix4,
        new Matrix4()
      );

      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4_2, new Matrix3());

      scale = Matrix4.getScale(matrix4_2, new Cartesian3());
      translation = Matrix4.getTranslation(matrix4_2, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    } else {
      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4, new Matrix3());

      scale = Matrix4.getScale(matrix4, new Cartesian3());
      translation = Matrix4.getTranslation(matrix4, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    }

    const result = {
      position: position,
      originalPosition: originalPosition,
      translation: translation,
      quaternion: quaternion,
      headingPitchRoll: headingPitchRoll,
      scale: scale,
    };

    return result;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {boolean} enu
   * @returns {HeadingPitchRoll}
   */
  static getHeadingPitchRoll(entity, enu = true) {
    const { headingPitchRoll } = this.decomposition(entity, enu);

    return headingPitchRoll;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {boolean} enu
   * @returns {Quaternion}
   */
  static getQuaternion(entity, enu = true) {
    const { quaternion } = this.decomposition(entity, enu);

    return quaternion;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {Quaternion | HeadingPitchRoll | Property} rotation
   * @returns {Quaternion | HeadingPitchRoll | Property}
   */
  static setRotation(entity, rotation) {
    if (rotation instanceof HeadingPitchRoll) {
      entity.orientation = Quaternion.fromHeadingPitchRoll(
        rotation,
        new Quaternion()
      );
    } else {
      entity.orientation = rotation;
    }

    return rotation;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @param {Cartesian3 | Property} scale
   * @returns {Cartesian3 | Property}
   */
  static setScale(entity, scale) {
    entity.scale = scale;
    return scale;
  }

  /**
   *
   * @param {PointEntity | LinestringEntity | PolygonEntity} entity
   * @returns {Cartesian3 | Property}
   */
  static getScale(entity) {
    const scale = Utils.getPropertyValue(entity.scale) || Cartesian3.ONE;
    return scale;
  }
}

export default EditDitapGeometry;
